// @ts-nocheck

/**
 * Section Shop Custom Element
 * Handles Swiper initialization and add-to-cart functionality
 */
class SectionShopElement extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.addToCartButtons = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.setupComponent()
      );
    } else {
      this.setupComponent();
    }
  }

  setupComponent() {
    this.initSwiper();
    this.initAddToCart();
    this.initSizeSelectors();
    this.initScrollGalleries();
    document.addEventListener("click", this.handleModalClose.bind(this));
  }

  initSwiper() {
    const swiperContainer = this.querySelector(".section-shop__swiper");
    if (!swiperContainer) return;

    // Check if Swiper is available
    if (typeof Swiper === "undefined") {
      console.warn("Swiper is not available");
      return;
    }

    this.swiper = new Swiper(swiperContainer, {
      slidesPerView: 1,
      spaceBetween: 30,
      navigation: {
        nextEl: ".section-shop__nav-next",
        prevEl: ".section-shop__nav-prev",
      },
      breakpoints: {
        768: {
          slidesPerView: 1,
          spaceBetween: 40,
        },
        1024: {
          slidesPerView: 1,
          spaceBetween: 50,
        },
      },
      on: {
        slideChange: () => {
          this.resetActiveSlideHint();
        },
      },
    });
  }

  initAddToCart() {
    this.addToCartButtons = this.querySelectorAll(".section-shop__add-button");

    this.addToCartButtons.forEach((button) => {
      button.addEventListener("click", this.handleAddToCart.bind(this));
    });
  }

  initSizeSelectors() {
    const sizeSelectors = this.querySelectorAll(".section-shop__size-selector");

    sizeSelectors.forEach((selector) => {
      const optionsContainer = selector.querySelector(
        ".section-shop__size-options"
      );
      const options = selector.querySelectorAll(
        ".section-shop__size-option[data-variant-id]"
      );
      const container = selector.closest(".section-shop__info-container");
      const sizeGuideButtons = container
        ? container.querySelectorAll(".section-shop__size-guide-link")
        : [];

      const allAddButtons = container?.querySelectorAll(
        ".section-shop__add-button"
      );

      if (!options.length) {
        return;
      }

      const setSelectedOption = (targetOption) => {
        options.forEach((option) => {
          option.classList.toggle("is-selected", option === targetOption);
          option.setAttribute(
            "aria-pressed",
            option === targetOption ? "true" : "false"
          );
        });

        const variantId = targetOption.dataset.variantId;
        const isAvailable = targetOption.dataset.available === 'true';

        if (variantId) {
          allAddButtons.forEach((btn) => {
            btn.dataset.productVariant = variantId;
            btn.disabled = !isAvailable;
          });
        }
      };

      options.forEach((option) => {
        option.addEventListener("click", () => {
          if (!option.dataset.variantId) return;
          setSelectedOption(option);
        });
      });

      const sizesDescription = container
        ? container.querySelector(".section-shop__sizes-description")
        : null;

      const enableMeasurements = () => {
        optionsContainer.classList.add("is-guide-active");
        if (sizesDescription) sizesDescription.classList.add("is-active");
      };

      const disableMeasurements = () => {
        optionsContainer.classList.remove("is-guide-active");
        if (sizesDescription) sizesDescription.classList.remove("is-active");
      };

      sizeGuideButtons.forEach((btn) => {
        btn.addEventListener("mouseenter", enableMeasurements);
        btn.addEventListener("mouseleave", disableMeasurements);
        btn.addEventListener("focus", enableMeasurements);
        btn.addEventListener("blur", disableMeasurements);
        btn.addEventListener("touchstart", enableMeasurements, {
          passive: true,
        });
        btn.addEventListener("touchend", disableMeasurements);
      });

      const presetSelected = selector.querySelector(
        ".section-shop__size-option.is-selected"
      );
      if (presetSelected) {
        setSelectedOption(presetSelected);
      } else {
        setSelectedOption(options[0]);
      }

      // --- Gallery Modal Logic ---
      const imageContainer = selector
        .closest(".section-shop__slide")
        ?.querySelector(".section-shop__image");

      const galleryDataScript = imageContainer?.querySelector(
        "[data-product-gallery]"
      );

      if (imageContainer && galleryDataScript) {
        imageContainer.addEventListener("click", (e) => {
          e.preventDefault(); // Prevent accidental navigation or bubbling
          e.stopPropagation();
          try {
            const gallery = JSON.parse(galleryDataScript.textContent);
            this.openGalleryModal(gallery);
          } catch (err) {
            console.error("Error parsing gallery data", err);
          }
        });
      }
    });
  }

  initScrollGalleries() {
    const scrollContainers = this.querySelectorAll(
      ".section-shop__image--scroll"
    );

    scrollContainers.forEach((slider) => {
      let isDown = false;
      let startY;
      let startX;
      let scrollTop;
      let isVertical = null; // null = undetermined, true = vertical, false = horizontal

      const reset = () => {
        isDown = false;
        slider.classList.remove("is-dragging");
        isVertical = null;
        if (this.swiper) this.swiper.allowTouchMove = true; // Always restore swiper
      };

      // Hide hint on first interaction
      const hideHint = () => {
        const wrapper = slider.closest(".section-shop__image-wrapper");
        const hint = wrapper?.querySelector(".section-shop__scroll-hint");
        if (hint) hint.classList.add("is-hidden");
      };

      slider.addEventListener("mousedown", (e) => {
        isDown = true;
        slider.classList.add("is-dragging");
        hideHint(); // Dismiss hint
        startY = e.pageY - slider.offsetTop;
        startX = e.pageX; // Document X
        scrollTop = slider.scrollTop;
        isVertical = null; // Reset direction check
        // Do NOT stop propagation. Let Swiper see the mousedown.
      });

      slider.addEventListener("mouseleave", reset);
      slider.addEventListener("mouseup", reset);

      slider.addEventListener("mousemove", (e) => {
        if (!isDown) return;

        const y = e.pageY - slider.offsetTop;
        const x = e.pageX;

        // Determine direction if not yet known
        if (isVertical === null) {
          const diffY = Math.abs(y - (startY + slider.offsetTop)); // diff from start Y
          const diffX = Math.abs(x - startX);

          // Need a small threshold to decide
          if (diffX < 5 && diffY < 5) return;

          if (diffY > diffX) {
            isVertical = true;
            if (this.swiper) this.swiper.allowTouchMove = false; // Lock Swiper
          } else {
            isVertical = false;
            // Leave Swiper enabled
          }
        }

        if (isVertical === true) {
          // we are handling the scroll
          e.preventDefault();
          // e.stopPropagation(); // Optional, but locking swiper is usually getting it done
          const walk = (y - startY) * 2;
          slider.scrollTop = scrollTop - walk;
        }
        // If isVertical === false, we do nothing; Swiper handles the horizontal drag
      });
    });
  }

  // Helper to open modal
  openGalleryModal(images) {
    const modal = document.getElementById("product-gallery-modal");
    const wrapper = document.getElementById("product-gallery-wrapper");
    if (!modal || !wrapper) return;

    // Clear existing
    wrapper.innerHTML = "";

    // Populate
    images.forEach((img) => {
      const slide = document.createElement("div");
      slide.classList.add("swiper-slide", "section-shop__modal-slide");

      const imageEl = document.createElement("img");
      imageEl.classList.add("section-shop__modal-image");
      imageEl.src = img.src;
      imageEl.alt = "Product Gallery Image";

      slide.appendChild(imageEl);
      wrapper.appendChild(slide);
    });

    // Show Modal
    modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden"; // Lock scroll

    // Check if Swiper is available globally
    if (typeof Swiper !== "undefined") {
      if (this.gallerySwiper) {
        this.gallerySwiper.destroy(true, true);
      }
      this.gallerySwiper = new Swiper(".section-shop__modal-swiper", {
        navigation: {
          nextEl: ".section-shop__modal-next",
          prevEl: ".section-shop__modal-prev",
        },
        pagination: {
          el: ".section-shop__modal-pagination",
          clickable: true,
        },
        loop: true,
      });
    }
  }

  handleModalClose(e) {
    if (e.target.matches("[data-modal-close], [data-modal-close] *")) {
      const modal = document.getElementById("product-gallery-modal");
      if (modal) {
        modal.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
      }
    }
  }

  async handleAddToCart(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const variantId = button.dataset.productVariant;
    const buttonIcon = button.querySelector(".section-shop__button-icon");
    const defaultIcon = button.querySelector(
      ".section-shop__heart-icon--default"
    );
    const variant2Icon = button.querySelector(
      ".section-shop__heart-icon--variant2"
    );

    if (!variantId) {
      console.error("No variant ID found");
      return;
    }

    // Disable button during request
    button.disabled = true;

    // Start transition
    buttonIcon.classList.add("transitioning");

    try {
      // Get sections to update for cart drawer
      const cartItemsComponents = document.querySelectorAll(
        "cart-items-component"
      );
      const sectionsToUpdate = new Set();
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      // Add item to cart with sections
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: Array.from(sectionsToUpdate).join(","),
          sections_url: window.location.pathname,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();

        // Success - show variant2 icon
        this.showSuccessState(defaultIcon, variant2Icon, buttonIcon, button);

        // Update cart count
        await this.updateCartCount();

        // Dispatch cart:update event for cart drawer integration
        const cartUpdateEvent = new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: variantId,
            data: {
              source: "section-shop-element",
              itemCount: 1,
              productId: productId,
              sections: responseData.sections || {},
            },
          },
        });
        document.dispatchEvent(cartUpdateEvent);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showErrorState(buttonIcon, button, error.message);
    }
  }

  showSuccessState(defaultIcon, variant2Icon, buttonIcon, button) {
    defaultIcon.style.opacity = "0";
    variant2Icon.style.opacity = "1";

    // Reset after 2 seconds
    setTimeout(() => {
      defaultIcon.style.opacity = "1";
      variant2Icon.style.opacity = "0";
      buttonIcon.classList.remove("transitioning");
      button.disabled = false;
    }, 2000);
  }

  showErrorState(buttonIcon, button, errorMessage) {
    // Reset button state
    buttonIcon.classList.remove("transitioning");
    button.disabled = false;

    // Show error message
    this.showNotification(
      errorMessage || "Error adding product to cart. Please try again.",
      "error"
    );
  }

  async updateCartCount() {
    try {
      const cartCountElements = document.querySelectorAll("[data-cart-count]");
      if (cartCountElements.length > 0) {
        const cartResponse = await fetch("/cart.js");
        const cartData = await cartResponse.json();
        cartCountElements.forEach((element) => {
          element.textContent = cartData.item_count;
        });
      }
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  showNotification(message, type = "info") {
    // Simple notification system
    const notification = document.createElement("div");
    notification.className = `shop-notification shop-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === "error" ? "#ff4444" : "#4CAF50"};
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      font-family: Arial, sans-serif;
    `;

    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }

  // Public method to reinitialize if needed
  reinit() {
    if (this.swiper) {
      this.swiper.destroy();
    }
    this.setupComponent();
  }
}

// Register the custom element
if (!customElements.get("section-shop")) {
  customElements.define("section-shop", SectionShopElement);
}
